import { getIdOrNull, getTrimmedOrNull, isOptimistic, validateString } from '../index'
import { normalizeName } from '../string'
import { Properties } from '../entities'

export const SchoolProperties = Object.freeze({
  ID: Properties.ID,
  ABBREVIATION: Properties.ABBREVIATION,
  ADDRESS: Properties.ADDRESS,
  COUNTRY: Properties.COUNTRY,
  EMAIL: Properties.EMAIL,
  NAME: Properties.NAME,
  PHONE_NUMBER: Properties.PHONE_NUMBER,
  PREMIUM: Properties.PREMIUM,
  VERIFIED: Properties.VERIFIED,
  MANAGED: Properties.MANAGED
})

export const SCHOOL_VALIDATORS = [
  { key: Properties.NAME, validator: s => validateString(s[Properties.NAME], { notEmpty: true }) },
  { key: Properties.ABBREVIATION, validator: s => validateString(s[Properties.ABBREVIATION], { notEmpty: true }) }
]

export const SchoolValidators = Object.freeze({
  [Properties.NAME]: s => validateString(s[Properties.NAME], { notEmpty: true }),
  [Properties.ABBREVIATION]: s => validateString(s[Properties.ABBREVIATION], { notEmpty: true }),
  [Properties.ADDRESS]: s => validateString(s[Properties.ADDRESS], { notEmpty: true }),
  [Properties.COUNTRY]: s => getIdOrNull(s[Properties.COUNTRY]) !== null || validateString(s[Properties.COUNTRY], { notEmpty: true })
})

export const isDescribedSchool = school => isOptimistic(school) && (school?.name ?? '') !== ''

/** Below that, an abbreviation is too short for a shared prefix to mean anything : "IN" is in half the names. */
const MIN_SIGNIFICANT_LENGTH = 3

/**
 * Two schools are the same one when they are named the same, abbreviated the same, or when one name is written
 * inside the other : "HELMo" and "HELMo Sainte-Julienne" are one school registered twice.
 */
export const findSimilarSchools = (school, schools = []) => {
  const name = normalizeName(school[Properties.NAME])
  const abbreviation = normalizeName(school[Properties.ABBREVIATION])

  if (name === '' && abbreviation === '') {
    return []
  }

  return schools.filter(existing => {
    const existingName = normalizeName(existing[Properties.NAME])
    const existingAbbreviation = normalizeName(existing[Properties.ABBREVIATION])

    if (name !== '' && existingName !== '' && (existingName === name ||
      (name.length >= MIN_SIGNIFICANT_LENGTH && existingName.includes(name)) ||
      (existingName.length >= MIN_SIGNIFICANT_LENGTH && name.includes(existingName)))
    ) {
      return true
    }

    return abbreviation !== '' && abbreviation.length >= MIN_SIGNIFICANT_LENGTH && existingAbbreviation === abbreviation
  })
}

export const getName = s => {
  return s.name
}

export const getAddress = s => {
  return s.address
}

export const getEmail = s => {
  return s.email
}

/** The API names it "phone" : a school read straight from a payload carries that name, not this entity's. */
export const getPhoneNumber = s => {
  return s.phoneNumber ?? s.phone ?? null
}

export class School {
  constructor ({ id, abbreviation, address, country, email, managed, name, phone, phoneNumber, premium, verified } = {}) {
    this.id = id ?? -1
    this.abbreviation = abbreviation ?? ''
    this.address = address ?? ''
    this.country = country ?? null
    this.email = email ?? null
    this.managed = managed ?? false
    this.name = name ?? ''
    this.phoneNumber = phoneNumber ?? phone ?? ''
    this.premium = premium ?? false
    this.verified = verified ?? false
  }

  toRequestBody = () => {
    const body = this.trimmed()

    body.country = getIdOrNull(this.country)
    body.phone = body.phoneNumber

    delete body.phoneNumber

    return body
  }

  trimmed = () => {
    const trimmed = { ...this }

    trimmed.name = getTrimmedOrNull(trimmed.name)
    trimmed.abbreviation = getTrimmedOrNull(trimmed.abbreviation)
    trimmed.address = getTrimmedOrNull(trimmed.address)
    trimmed.email = getTrimmedOrNull(trimmed.email)
    trimmed.phoneNumber = getTrimmedOrNull(trimmed.phoneNumber)

    return trimmed
  }
}
